import { useQuery } from '@tanstack/react-query';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAtomValue } from 'jotai';

import { COGNITO_GROUPS } from '@/constants/cognitoGroups';
import { userIdAtom } from '@/services/globalState';

async function getCognitoGroups(): Promise<string[]> {
  try {
    const session = await fetchAuthSession();

    const groups = session.tokens?.accessToken?.payload['cognito:groups'] as string[] | undefined;

    return groups ?? [];
  } catch (error) {
    return [];
  }
}

/**
 * Group membership for one signed-in identity, keyed by that identity.
 *
 * The key carries the user id because the answer is about a specific session and nothing
 * clears this cache when the session changes: `services/session.ts` never touches the query
 * client, so an entry written before sign-in would otherwise still be served after it. That
 * is not hypothetical — `getAlgoliaExperts` primes this query from the *public* directory,
 * the page every signed-out visitor lands on, where `fetchAuthSession` has no session and
 * the answer is an empty list. Under a shared key the reader who then signs in reads that
 * empty list back as settled data, and staff-only UI stays hidden until a refetch lands.
 *
 * A separate key per identity makes the stale entry unreachable rather than merely
 * short-lived: the signed-in reader looks up a key nothing has written yet, so the hook
 * reports the answer as unresolved, which is what its consumers gate on.
 */
export function cognitoGroupsQueryOptions(userId?: string | null) {
  return {
    queryKey: ['cognitoGroups', userId ?? null],
    queryFn: getCognitoGroups,
  };
}

/**
 * Cognito group membership for the current session.
 *
 * `isGroupMembershipResolved` is the field to gate on, not `isLoading`. The two differ in
 * the case that matters: a query holding no data for this identity but data for another
 * reads as settled and not loading, so `isLoading` says "we know" when the honest answer is
 * "not for this reader". Anything that hides UI or drops a route on membership should stay
 * open while this is false, because every unresolved state here reports `isPrimumUser: false`.
 *
 * Gated on a Cognito session rather than on `shouldFetchUserScoped`. Groups are claims on
 * the access token, so they exist for a pending-OAuth user who has no database record yet,
 * and borrowing the database-user predicate would blank the groups of a reader who has them.
 */
export function useCognitoGroups() {
  const userId = useAtomValue(userIdAtom);

  const query = useQuery({
    ...cognitoGroupsQueryOptions(userId),
    enabled: !!userId,
  });

  const groups: string[] = Array.isArray(query.data) ? query.data : [];

  const isDevUser = groups.includes(COGNITO_GROUPS.DEV);

  const isPrimumUser = groups.includes(COGNITO_GROUPS.PRIMUM);

  const isOperationsUser = groups.includes(COGNITO_GROUPS.OPERATIONS);

  const isPharmaUser = groups.includes(COGNITO_GROUPS.PHARMA);

  return {
    ...query,
    groups,
    isDevUser,
    isPrimumUser,
    isOperationsUser,
    isPharmaUser,
    isGroupMembershipResolved: query.isSuccess,
  };
}
